/**
 * La cara SÍNCRONA de Sentry. Este archivo no importa Sentry: lo pide después del primer pintado.
 *
 * 🚨 SENTRY PESABA 935 KB DENTRO DEL CHUNK DE ARRANQUE. Medido sobre el sourcemap del bundle:
 *    `@sentry/core` 394 KB · `replay` 300 KB · `browser-utils` 134 KB · `browser` 107 KB. Todo el
 *    que abría el panel descargaba, interpretaba y EJECUTABA casi un megabyte de monitoreo antes de
 *    ver la primera fila de su lista — y ese costo se paga en CPU, así que ni siquiera desaparece
 *    en una conexión rápida.
 *
 * ⭐ AHORA EL MONITOREO LLEGA TARDE Y NO SE PIERDE NADA. Lo que ocurra mientras carga se encola en
 *    `sentryPuente` y se entrega cuando el módulo real se registra.
 *
 * 🪤 SE CARGA EN `requestIdleCallback`, NO EN UN `setTimeout(0)`. Con timeout compite con el primer
 *    render por el mismo hilo: se corre el bloqueo unos milisegundos y se lo devuelve justo cuando
 *    la pantalla intenta pintarse. `requestIdleCallback` espera a que el navegador esté libre.
 *
 * 🪤 Y SIN DSN NO SE CARGA NADA. En desarrollo el módulo pesado no se pide siquiera: la variable
 *    sólo está definida en producción.
 */

import { capturarExcepcion, identificarUsuario, olvidarUsuario } from '@/lib/sentryPuente';

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const hayDsn = typeof dsn === 'string' && dsn.length > 0;

if (hayDsn) {
  const cargar = () => {
    void import('@/lib/sentryReal')
      .then((m) => m.iniciarSentry(dsn as string))
      // Si el monitoreo no carga, la app sigue: no puede ser Sentry quien rompa la pantalla.
      .catch(() => {});
  };
  const idle = (globalThis as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void })
    .requestIdleCallback;
  if (typeof idle === 'function') idle(cargar, { timeout: 4000 });
  else setTimeout(cargar, 2000);
}

/** Reportar un error manualmente (ej. desde ErrorBoundary). Se encola si Sentry todavía no cargó. */
export function reportError(
  error: Error,
  context?: { componentStack?: string; extra?: Record<string, unknown> },
): void {
  if (!hayDsn) return;
  capturarExcepcion(error, {
    ...(context?.extra ?? {}),
    ...(context?.componentStack ? { componentStack: context.componentStack } : {}),
  });
}

/** Identificar al usuario actual en Sentry (tenant, rol). Llamar tras resolver auth. */
export function setSentryUser(user: { id: string; tenantId: string; role: string; email?: string }): void {
  if (!hayDsn) return;
  identificarUsuario(user);
}

/** Limpiar identidad de Sentry (logout). */
export function clearSentryUser(): void {
  if (!hayDsn) return;
  olvidarUsuario();
}
